/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import logoArabic from "../../assets/logoArabic.png";
import logoEnglish from "../../assets/logoEnglish.png";
import LegalDocumentModal from "../../components/legal/LegalDocumentModal";
import { LEGAL_DOCUMENTS } from "../../utils/legalDocuments";
import { AUTH_COPY, PASSWORD_RULES } from "./authCopy";
import "./auth.css";

function IraqFlag() {
  return (
    <svg className="flag" viewBox="0 0 21 14" aria-hidden="true">
      <rect width="21" height="14" rx="2.5" fill="#fff" />
      <path d="M0 2.5A2.5 2.5 0 0 1 2.5 0h16A2.5 2.5 0 0 1 21 2.5v2.2H0z" fill="#CE1126" />
      <path d="M0 9.3h21v2.2a2.5 2.5 0 0 1-2.5 2.5h-16A2.5 2.5 0 0 1 0 11.5z" fill="#292929" />
      <path
        d="M5.2 6.2c.5-.5 1.2-.5 1.6 0M9.7 6.2c.5-.5 1.2-.5 1.6 0M14.2 6.2c.5-.5 1.2-.5 1.6 0"
        stroke="#007A3D"
        strokeWidth="1.1"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M4 12.5l5 5L20 6.5" />
    </svg>
  );
}

const LEGAL_THEME = {
  darkPurple: "#112758",
  purple: "#0E3981",
  gold: "#0E3981",
  textLight: "#f3e9ff",
  gradient: "linear-gradient(135deg,#0E3981,#112758)",
};

export default function AuthUI({
  isArabic,
  mode,
  setMode,
  loginIdMode,
  setLoginIdMode,
  signup,
  login,
  errors,
  clearError,
  handleChange,
  handleSignupSubmit,
  handleLoginSubmit,
  loading,
  formError,
  showPass,
  setShowPass,
  showPass2,
  setShowPass2,
  showPassLi,
  setShowPassLi,
  handleValid,
  legalOpen,
  legalDoc,
  legalContent,
  legalLoading,
  openLegal,
  closeLegal,
  toggleLanguage,
}) {
  const t = AUTH_COPY[isArabic ? "ar" : "en"];
  const fieldClass = (key) => `madar-auth-field${errors[key] ? " invalid" : ""}`;

  return (
    <div className="madar-auth" dir={isArabic ? "rtl" : "ltr"}>
      <svg className="madar-auth-orbits" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <ellipse cx="140" cy="120" rx="220" ry="120" transform="rotate(-16 140 120)" />
        <ellipse cx="1320" cy="200" rx="160" ry="260" transform="rotate(20 1320 200)" />
        <ellipse cx="180" cy="820" rx="260" ry="140" transform="rotate(-8 180 820)" />
        <ellipse cx="1300" cy="780" rx="130" ry="230" transform="rotate(28 1300 780)" />
      </svg>

      <div className="madar-auth-wrap">
        <div className="madar-auth-topbar">
          <a href="/" className="madar-auth-logo" aria-label="Madar">
            <img src={isArabic ? logoArabic : logoEnglish} alt="Madar" />
          </a>
          <button className="madar-auth-lang" type="button" onClick={toggleLanguage}>
            {t.langBtn}
          </button>
        </div>

        <div className="madar-auth-card">
          <div className="madar-auth-mode" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className={mode === "login" ? "active" : ""}
              onClick={() => setMode("login")}
            >
              {t.modeLogin}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signup"}
              className={mode === "signup" ? "active" : ""}
              onClick={() => setMode("signup")}
            >
              {t.modeSignup}
            </button>
          </div>

          {formError ? <div className="madar-auth-banner error">{formError}</div> : null}

          {mode === "signup" ? (
            <div>
              <h2>{t.suTitle}</h2>
              <p className="madar-auth-sub">{t.suSub}</p>

              <form className="madar-auth-form" onSubmit={handleSignupSubmit} noValidate>
                <div className="madar-auth-row2">
                  <div className={fieldClass("fullName")}>
                    <label htmlFor="auth-name">{t.nameL}</label>
                    <div className="madar-auth-control">
                      <input
                        id="auth-name"
                        name="fullName"
                        type="text"
                        autoComplete="name"
                        placeholder={t.nameP}
                        value={signup.fullName}
                        onChange={handleChange}
                      />
                    </div>
                    <p className="madar-auth-err">{t.nameE}</p>
                  </div>
                  <div className={fieldClass("storeName")}>
                    <label htmlFor="auth-store">{t.storeL}</label>
                    <div className="madar-auth-control">
                      <input
                        id="auth-store"
                        name="storeName"
                        type="text"
                        placeholder={t.storeP}
                        value={signup.storeName}
                        onChange={handleChange}
                      />
                    </div>
                    <p className="madar-auth-hint">{t.storeH}</p>
                    <p className="madar-auth-err">{t.storeE}</p>
                  </div>
                </div>

                <div className="madar-auth-row2">
                  <div className={fieldClass("phone")}>
                    <label htmlFor="auth-phone">{t.phoneL}</label>
                    <div className="madar-auth-control madar-auth-phone">
                      <span className="madar-auth-prefix">
                        <IraqFlag /> +964
                      </span>
                      <input
                        id="auth-phone"
                        name="phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="7XX XXX XXXX"
                        style={{ direction: "ltr", textAlign: "left" }}
                        value={signup.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <p className="madar-auth-hint" dangerouslySetInnerHTML={{ __html: t.phoneH }} />
                    <p className="madar-auth-err">{t.phoneE}</p>
                  </div>
                  <div className={fieldClass("email")}>
                    <label htmlFor="auth-email">{t.emailL}</label>
                    <div className="madar-auth-control">
                      <input
                        id="auth-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="name@example.com"
                        style={{ direction: "ltr" }}
                        value={signup.email}
                        onChange={handleChange}
                      />
                    </div>
                    <p className="madar-auth-err">{t.emailE}</p>
                  </div>
                </div>

                <div className={fieldClass("storeSlug")}>
                  <label htmlFor="auth-handle">
                    <span>{t.userL}</span> <span className="tag">{t.userTag}</span>
                  </label>
                  <div className="madar-auth-control madar-auth-handle">
                    <input
                      id="auth-handle"
                      name="storeSlug"
                      type="text"
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      placeholder="alnoor-store"
                      style={{ direction: "ltr", textAlign: "left" }}
                      value={signup.storeSlug}
                      onChange={handleChange}
                    />
                    <span className="madar-auth-suffix">.madar.iq</span>
                  </div>
                  <p className="madar-auth-hint" dangerouslySetInnerHTML={{ __html: t.userH }} />
                  <p className="madar-auth-err">
                    {typeof errors.storeSlug === "string" ? errors.storeSlug : t.userE}
                  </p>
                </div>

                <div className={`madar-auth-linkbox${handleValid ? " good" : ""}`}>
                  <svg className="globe" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3z" />
                  </svg>
                  <div className="lb-text">
                    <div className="lb-label">{t.lbLabel}</div>
                    <div className="lb-url">
                      https://<span className="u">{signup.storeSlug || "yourstore"}</span>.madar.iq
                    </div>
                  </div>
                  <span className="ok-badge">
                    <CheckIcon />
                    <span>{t.lbOk}</span>
                  </span>
                </div>

                <div className={fieldClass("password")}>
                  <label htmlFor="auth-pass">{t.passL}</label>
                  <div className="madar-auth-control">
                    <input
                      id="auth-pass"
                      name="password"
                      type={showPass ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder={t.passP}
                      style={{ direction: "ltr" }}
                      value={signup.password}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      className="madar-auth-eye"
                      aria-label={t.passL}
                      onClick={() => setShowPass((v) => !v)}
                    >
                      <EyeIcon />
                    </button>
                  </div>
                  <div className="madar-auth-pw-rules">
                    {Object.keys(PASSWORD_RULES).map((rule) => (
                      <div
                        key={rule}
                        className={`madar-auth-rule${PASSWORD_RULES[rule](signup.password) ? " met" : ""}`}
                      >
                        <span className="dot">
                          <CheckIcon />
                        </span>
                        <span>
                          {rule === "len" ? t.pwLen : rule === "case" ? t.pwCase : rule === "num" ? t.pwNum : t.pwSym}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="madar-auth-err">{t.passE}</p>
                </div>

                <div className={fieldClass("confirmPassword")}>
                  <label htmlFor="auth-pass2">{t.pass2L}</label>
                  <div className="madar-auth-control">
                    <input
                      id="auth-pass2"
                      name="confirmPassword"
                      type={showPass2 ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder={t.pass2P}
                      style={{ direction: "ltr" }}
                      value={signup.confirmPassword}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      className="madar-auth-eye"
                      aria-label={t.pass2L}
                      onClick={() => setShowPass2((v) => !v)}
                    >
                      <EyeIcon />
                    </button>
                  </div>
                  <p className="madar-auth-err">{t.pass2E}</p>
                </div>

                <button className="madar-auth-cta" type="submit" disabled={loading}>
                  {loading ? t.suLoading : t.suCta}
                </button>

                <p className="madar-auth-terms">
                  <span>{t.termsA} </span>
                  <button type="button" className="link" onClick={() => openLegal("terms")}>
                    {t.termsB}
                  </button>
                  <span> {t.termsC} </span>
                  <button type="button" className="link" onClick={() => openLegal("privacy")}>
                    {t.termsD}
                  </button>
                </p>
              </form>
            </div>
          ) : (
            <div>
              <h2>{t.liTitle}</h2>
              <p className="madar-auth-sub">{t.liSub}</p>

              <form className="madar-auth-form" onSubmit={handleLoginSubmit} noValidate>
                <div className="madar-auth-seg" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={loginIdMode === "phone"}
                    className={loginIdMode === "phone" ? "active" : ""}
                    onClick={() => {
                      setLoginIdMode("phone");
                      clearError("loginPhone");
                      clearError("loginEmail");
                    }}
                  >
                    <svg viewBox="0 0 24 24">
                      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
                      <path d="M11 18.5h2" />
                    </svg>
                    <span>{t.tabPhone}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={loginIdMode === "email"}
                    className={loginIdMode === "email" ? "active" : ""}
                    onClick={() => {
                      setLoginIdMode("email");
                      clearError("loginPhone");
                      clearError("loginEmail");
                    }}
                  >
                    <svg viewBox="0 0 24 24">
                      <rect x="3" y="5" width="18" height="14" rx="2.5" />
                      <path d="m4 7 8 6 8-6" />
                    </svg>
                    <span>{t.tabEmail}</span>
                  </button>
                </div>

                {loginIdMode === "phone" ? (
                  <div className={fieldClass("loginPhone")}>
                    <label htmlFor="auth-phone-li">{t.phoneL}</label>
                    <div className="madar-auth-control madar-auth-phone">
                      <span className="madar-auth-prefix">
                        <IraqFlag /> +964
                      </span>
                      <input
                        id="auth-phone-li"
                        name="loginPhone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="7XX XXX XXXX"
                        style={{ direction: "ltr", textAlign: "left" }}
                        value={login.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <p className="madar-auth-hint" dangerouslySetInnerHTML={{ __html: t.phoneH }} />
                    <p className="madar-auth-err">{t.phoneE}</p>
                  </div>
                ) : (
                  <div className={fieldClass("loginEmail")}>
                    <label htmlFor="auth-email-li">{t.emailL}</label>
                    <div className="madar-auth-control">
                      <input
                        id="auth-email-li"
                        name="loginEmail"
                        type="email"
                        autoComplete="email"
                        placeholder="name@example.com"
                        style={{ direction: "ltr", textAlign: "left" }}
                        value={login.email}
                        onChange={handleChange}
                      />
                    </div>
                    <p className="madar-auth-err">{t.emailE}</p>
                  </div>
                )}

                <div className={fieldClass("loginPassword")}>
                  <div className="madar-auth-label-row">
                    <label htmlFor="auth-pass-li">{t.passL}</label>
                    <a href="/reset-password">{t.forgot}</a>
                  </div>
                  <div className="madar-auth-control">
                    <input
                      id="auth-pass-li"
                      name="loginPassword"
                      type={showPassLi ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder={t.passPLi}
                      style={{ direction: "ltr" }}
                      value={login.password}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      className="madar-auth-eye"
                      aria-label={t.passL}
                      onClick={() => setShowPassLi((v) => !v)}
                    >
                      <EyeIcon />
                    </button>
                  </div>
                  <p className="madar-auth-err">{t.passELi}</p>
                </div>

                <button className="madar-auth-cta" type="submit" disabled={loading}>
                  {loading ? t.liLoading : t.liCta}
                </button>

                <p className="madar-auth-terms">
                  <span>{t.noAcc} </span>
                  <button type="button" className="link" onClick={() => setMode("signup")}>
                    {t.goSignup}
                  </button>
                </p>
              </form>
            </div>
          )}
        </div>
      </div>

      <LegalDocumentModal
        isOpen={legalOpen}
        onClose={closeLegal}
        title={
          isArabic
            ? LEGAL_DOCUMENTS[legalDoc]?.titleAr
            : LEGAL_DOCUMENTS[legalDoc]?.titleEn
        }
        content={legalContent}
        loading={legalLoading}
        isArabic={isArabic}
        themeColors={LEGAL_THEME}
      />
    </div>
  );
}
